import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import OptionsModelEntity from './OptionsMode';
import { Separator } from '../ui/separator';
import {
  PiArrowsClockwiseLight,
  PiChartBarLight,
  PiDownloadSimpleLight,
  PiFileArrowDownLight,
  PiFileArrowUpLight,
  PiFileCsvLight,
  PiGlobeLight,
  PiShareFatLight,
  PiShootingStarLight,
  PiSignOutLight,
  PiSpeakerHighLight,
  PiSpeakerSlashLight,
  PiTelegramLogoLight,
  PiUploadSimpleLight,
  PiUserCircleThin,
  PiWaveSineLight,
  PiGameControllerThin,
  PiVideoCameraLight
} from 'react-icons/pi';
import { FiSave } from "react-icons/fi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { IconButton } from '../ui/button';
import { useSession, signOut, signIn } from 'next-auth/react';
import { PiSlidersThin } from 'react-icons/pi';
import { useManagerEffect, useManagerEntity } from '@/shared/managerHelpers';
import { Switch } from '../ui/switch';
import { isAdminPage } from '@/sharedTypes/helpers';
import { usePathname } from 'next/navigation';
import { useGameScoring } from '@/hooks/useGamification';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu';
import { saveDebugData } from '../Debug/DebugRecorder';
import { ManagerEntity } from '@/shared/globalFunctions';
import DebugModelEntity from '../Debug/DebugModel';
import i18n from '@/shared/i18n';
import { useTranslation } from 'react-i18next';
import { apiSwaggerClientSide } from '@/shared/api';
import toast from '../ui/custom-toast';
import Link from 'next/link';
import TooltipMidiInput from '../Tooltips/TooltipMidiInput';
import TooltipOptions from '../Tooltips/TooltipOptions';
import TooltipSaveProgress from '../Tooltips/TooltipSaveProgress';
import Avatar from '../Account/Avatar';
import { useGlobalAvatar } from '@/hooks/useGlobalAvatar';
import { useMidiDeviceSelector } from '@/hooks/useMidiDeviceSelector';
import { useIsUserAuthorized } from '@/utils/authHelpers';
import { useTourStarter } from '@/hooks/useTourStarter';
import etaLoader from '@/lib/EtaLoader';
import StatsModal from '../Stats/StatsModal';
import { NEXT_PUBLIC_FRONTEND_URL } from '@/shared/envs';

const BASE_URL = NEXT_PUBLIC_FRONTEND_URL || 'https://darpiano.com';

export const Options = observer(() => {
  const optionsRef = useRef<HTMLDivElement>(null);
  const optionsTriggerRef = useRef<HTMLDivElement>(null);
  const managerEntity = useManagerEntity();
  const { startTour } = useTourStarter();
  const pathname = usePathname();
  const { gamificationEnabled, toggleGamification, saveProgressEnabled, toggleSaveProgress } = useGameScoring({sampleId: null, userId: null});
  // Use the new MIDI device selector hook
  const {
    selectedDeviceId,
    setSelectedDeviceId,
    midiInputs,
    refreshMidiInputs,
  } = useMidiDeviceSelector(managerEntity);

  const session = useSession();

  const [actualDisplayName, setActualDisplayName] = useState<
    string | undefined
  >(undefined);

  const isUserAuthorized = useIsUserAuthorized();
  const hasLoadedUserDetails = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('initializingAccount', 0);
    }
    // Only make API call if user is authorized and we haven't loaded yet
    if (!isUserAuthorized) {
      console.log('User not authorized, skipping user details API call');
      hasLoadedUserDetails.current = false; // Reset when user becomes unauthorized
      return;
    }

    // Prevent duplicate calls
    if (hasLoadedUserDetails.current) {
      console.log('User details already loaded, skipping API call');
      return;
    }

    console.log('Loading user details for authorized user');
    hasLoadedUserDetails.current = true;

    if (typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('initializingAccount', 0.4);
    }

    apiSwaggerClientSide.user
      .detailsList()
      .then((res) => {
        console.log('User details API response:', res.status);
        if (res.data?.displayName) {
          setActualDisplayName(res.data.displayName);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch user details:', err);
        hasLoadedUserDetails.current = false; // Reset on error to allow retry
      });
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('initializingAccount', 0.7);
      }
  }, [isUserAuthorized]);

  const username = actualDisplayName || session?.data?.user?.displayName;

  // Use MobX
  const { avatarUrl, loading: avatarLoading } = useGlobalAvatar();
  if (typeof window !== 'undefined' && etaLoader) {
    etaLoader.setStage('initializingAccount', 1);
  }

  OptionsModelEntity.optionsRef = optionsRef;
  OptionsModelEntity.optionsBtnRef = optionsTriggerRef;

  const [, forceUpdate] = useState({});
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const { t } = useTranslation();
  const languageLabels: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
  };

  const [muted, setMuted] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('midi_output_muted');
      return stored === 'true';
    }
    // fallback for SSR
    return managerEntity?.player?.mute ?? false;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('midi_output_muted', String(muted));
    }
    if (managerEntity?.player) {
      managerEntity.player.mute = muted;
    }
  }, [muted, managerEntity]);

  useEffect(() => {
    const onLangChanged = () => forceUpdate({});
    i18n.on('languageChanged', onLangChanged);
    return () => i18n.off('languageChanged', onLangChanged);
  }, []);

  // Handle browser back/forward buttons for practice pages
  useEffect(() => {
    if (!pathname.includes('/practice')) return;

    const handlePopState = () => {
      // Detect language from current URL after back/forward navigation
      const isRussian = window.location.pathname.startsWith('/ru/practice');
      const targetLang = isRussian ? 'ru' : 'en';

      // Update i18n if language changed
      if (i18n.language !== targetLang) {
        i18n.changeLanguage(targetLang);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [pathname]);

  const handleLangChange = (newLang: string) => {
    // Update i18n language - this will trigger re-render with new translations
    i18n.changeLanguage(newLang);

    // For practice pages, change URL without navigation to preserve player state
    if (pathname.includes('/practice')) {
      const searchParams = new URLSearchParams(window.location.search);
      const musicId = searchParams.get('m');

      // Construct new URL based on target language
      const newUrl = newLang === 'ru'
        ? `/ru/practice${musicId ? `?m=${musicId}` : ''}`
        : `/practice${musicId ? `?m=${musicId}` : ''}`;

      // Only change URL if it's actually different
      if (window.location.pathname + window.location.search !== newUrl) {
        // Change URL without triggering Next.js navigation to preserve player state
        window.history.pushState(null, '', newUrl);
      }

      return;
    }

    // For account, admin, and debug pages, just change language without navigation
    if (pathname.includes('/account') || pathname.includes('/admin') || pathname.includes('/debug')) {
      return;
    }
  };

  useManagerEffect(() => {
    refreshMidiInputs();
  }, [refreshMidiInputs]);

  const importCsvInputRef = useRef<HTMLInputElement>(null);
  const uploadSeoInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get sitemap URL count and calculate processing time
  const getSitemapCountAndEstimateTime = async (sitemapUrl: string = `${BASE_URL}/sitemap.xml`) => {
    try {
      const response = await fetch(sitemapUrl);
      const xmlText = await response.text();
      
      // Count <loc> tags in the sitemap
      const locMatches = xmlText.match(/<loc>/g);
      const urlCount = locMatches ? locMatches.length : 0;
      
      // Calculate estimated processing time based on backend logic:
      // - Batch size: 5 URLs per batch
      // - Delay: 1 second between batches
      // - Average processing time per URL: ~2-3 seconds
      const batchCount = Math.ceil(urlCount / 5);
      const delayTime = Math.max(0, batchCount - 1); // No delay after last batch
      const processingTime = urlCount * 2.5; // ~2.5 seconds per URL on average
      const totalSeconds = processingTime + delayTime;
      const totalMinutes = totalSeconds / 60;
      
      return {
        urlCount,
        estimatedMinutes: Math.max(totalMinutes, 1), // At least 1 minute minimum
        estimatedSeconds: Math.max(totalSeconds, 60)
      };
    } catch (error) {
      console.error('Error fetching sitemap:', error);
      // Fallback to conservative estimate
      return {
        urlCount: 1100,
        estimatedMinutes: 6,
        estimatedSeconds: 360
      };
    }
  };

  const handleImportCSV = async (file: File) => {
    // Check if user is authorized before making API call
    if (!isUserAuthorized) {
      toast({
        title: t('toast.error_title'),
        description: 'You need to be logged in to import CSV files',
        type: 'error',
      });
      return;
    }

    ManagerEntity.spinnerMsg = 'Loading CSV...';
    try {
      // Log file details for debugging
      console.log('Selected file:', file);
      console.log('File size:', file.size);
      console.log('File type:', file.type);

      // Validate file
      if (!file.name.toLowerCase().endsWith('.csv')) {
        toast({
          title: t('toast.error_title'),
          description: 'Please select a valid CSV file',
          type: 'error',
        });
        return;
      }

      if (file.size === 0) {
        toast({
          title: t('toast.error_title'),
          description: 'The selected file is empty',
          type: 'error',
        });
        return;
      }

      // Read file content as text
      const csvContent = await file.text();
      console.log('CSV content length:', csvContent.length);
      console.log('CSV content preview:', csvContent.substring(0, 200) + '...');

      if (!csvContent.trim()) {
        toast({
          title: t('toast.error_title'),
          description: 'The CSV file appears to be empty',
          type: 'error',
        });
        return;
      }
      // Make the API call
      const response = await apiSwaggerClientSide.samples.importcsvCreate({
        csvContent,
      });

      // Log the server response
      console.log('Server response:', response);

      // Show success toast
      toast({
        title: t('toast.success_title'),
        description:
          t('toast.csv_import_success_desc') +
          ' ' +
          response.data.results.updated,
        type: 'success',
      });
    } catch (error) {
      // Log the error details
      console.error('Error importing CSV:', error);

      // Show error toast
      toast({
        title: t('toast.error_title'),
        description: t('toast.csv_import_error_desc'),
        type: 'error',
      });
    } finally {
      // Clear spinner
      ManagerEntity.spinnerMsg = '';
    }
  };

  const handleExportCSV = async () => {
    // Check if user is authorized before making API call
    if (!isUserAuthorized) {
      toast({
        title: t('toast.error_title'),
        description: 'You need to be logged in to export CSV files',
        type: 'error',
      });
      return;
    }

    try {
      const response = await apiSwaggerClientSide.samples.exportcsvList();

      const text = await response.text();
      console.log('text: ', text);
      // Create a download link
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `samples_export_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: t('toast.success_title'),
        description: t('toast.csv_export_success_desc'),
        type: 'success',
      });
    } catch (error) {
      ManagerEntity.spinnerMsg = '';
      console.error('Error exporting CSV:', error);
      toast({
        title: t('toast.error_title'),
        description: t('toast.csv_export_error_desc'),
        type: 'error',
      });
    }
  };

  const handleUploadSEO = async (file: File) => {
    // Check if user is authorized before making API call
    if (!isUserAuthorized) {
      toast({
        title: t('toast.error_title'),
        description: 'You need to be logged in to upload CSV files',
        type: 'error',
      });
      return;
    }

    ManagerEntity.spinnerMsg = 'Uploading CSV...';
    
    try {
      // Validate file
      if (!file.name.toLowerCase().endsWith('.csv')) {
        toast({
          title: t('toast.error_title'),
          description: 'Please select a valid CSV file',
          type: 'error',
        });
        return;
      }

      if (file.size === 0) {
        toast({
          title: t('toast.error_title'),
          description: 'The selected file is empty',
          type: 'error',
        });
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('csvFile', file);
      
      // Make the API call to store metadata CSV using Swagger client
      const response = await apiSwaggerClientSide.admin.storeMetadataCsvCreate(
        formData as any
      );
      
      // Handle response
      if(response.ok) {
        toast({
          title: t('toast.success_title'),
          description: `CSV uploaded successfully! ${response.data?.rowCount || ''} rows processed.`,
          type: 'success',
        });
      } else {
        toast({
          title: t('toast.error_title'),
          description: 'Failed to upload CSV',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast({
        title: t('toast.error_title'),
        description: 'Failed to upload CSV file',
        type: 'error',
      });
    } finally {
      ManagerEntity.spinnerMsg = '';
    }
  };

  const handleExportSEO = async () => {
    // Check if user is authorized before making API call
    if (!isUserAuthorized) {
      toast({
        title: t('toast.error_title'),
        description: 'You need to be logged in to export CSV files',
        type: 'error',
      });
      return;
    }

    // Start the ETA context for CSV export
    if (typeof window !== 'undefined' && etaLoader) {
      etaLoader.setContext('exportCSV');
      etaLoader.setStage('exportCSV_processingUrls', 0);
    }

    try {
      // Get dynamic timing estimate based on actual sitemap
      const { urlCount, estimatedMinutes, estimatedSeconds } = await getSitemapCountAndEstimateTime();
      console.log(`📊 Sitemap contains ${urlCount} URLs, estimated processing time: ${estimatedMinutes.toFixed(1)} minutes`);

      // Create dynamic label with URL count and estimated time
      const dynamicLabel = t('loading.exportCSV.exportCSV_processingUrls', {
        urlCount: urlCount,
        estimatedMinutes: Math.ceil(estimatedMinutes)
      });

      // Start progress simulation for the long server operation
      let text: string;
      if (typeof window !== 'undefined' && etaLoader) {
        
        let progressValue = 0;
        const startTime = Date.now();
        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const minutes = elapsed / (1000 * 60);

          // Use dynamic timing based on actual sitemap size
          if (minutes < estimatedMinutes) {
            // Logarithmic progress curve adjusted for actual estimated time
            const rate = 0.8 / estimatedMinutes * 6; // Normalize rate based on estimated vs original 6 minutes
            progressValue = Math.min(0.95, 1 - Math.exp(-minutes * rate));
            etaLoader.setStage('exportCSV_processingUrls', progressValue, dynamicLabel);
          }
        }, 2000);

        // Wait for the response to complete (this is the slow operation)
        const response = await apiSwaggerClientSide.admin.exportMetadataCsvList();
        text = await response.text();
        clearInterval(progressInterval);
      } else {
        const response = await apiSwaggerClientSide.admin.exportMetadataCsvList();
        text = await response.text();
      }
      
      // Create a download link (this is quick)
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `SEO_export_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);

      // Complete the stage
      if (typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('exportCSV_processingUrls', 1);
      }

      toast({
        title: t('toast.success_title'),
        description: t('toast.csv_export_success_desc'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast({
        title: t('toast.error_title'),
        description: t('toast.csv_export_error_desc'),
        type: 'error',
      });
    }
  };

  const handleDownloadSEO = async () => {
    // Check if user is authorized before making API call
    if (!isUserAuthorized) {
      toast({
        title: t('toast.error_title'),
        description: 'You need to be logged in to download CSV files',
        type: 'error',
      });
      return;
    }

    ManagerEntity.spinnerMsg = 'Downloading latest CSV...';
    try {
      // Download the latest stored CSV with enhancements
      const response = await apiSwaggerClientSide.admin.downloadLatestCsvList({
        includeTestDocs: 'false' // Set to 'true' to include test documents
      });

      const text = await response.text();
      
      // Create a download link
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `SEO_download_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);

      toast({
        title: t('toast.success_title'),
        description: 'Latest CSV downloaded successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      
      // Handle specific error cases
      if (error && typeof error === 'object' && 'response' in error && 
          error.response && typeof error.response === 'object' && 
          'status' in error.response && error.response.status === 404) {
        toast({
          title: t('toast.error_title'),
          description: 'No CSV files found in database',
          type: 'error',
        });
      } else {
        toast({
          title: t('toast.error_title'),
          description: 'Failed to download latest CSV',
          type: 'error',
        });
      }
    } finally {
      ManagerEntity.spinnerMsg = '';
    }
  }

  const handleStartTour = () => {
    startTour(() => {
      OptionsModelEntity.showOptions = false;
    });
  };

  const hasDebugData = Boolean(
    managerEntity?.player?.eventsRecorder?.debugData?.events &&
      Object.keys(managerEntity.player.eventsRecorder.debugData.events).length >
        0
  );

  return (
    <>
    <DropdownMenu>
      <TooltipOptions>
        <DropdownMenuTrigger asChild>
          <IconButton data-testid='options_toggle_btn'>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <PiSlidersThin size={36} />
          </IconButton>
        </DropdownMenuTrigger>
      </TooltipOptions>
      <DropdownMenuContent className='min-w-[22rem] overflow-hidden flex flex-col h-fit max-h-[25dvh] md:max-h-[80vh]'>
        <div className='py-1 px-2 md:py-3 flex items-center gap-2 border-b bg-white'>
          {session?.data?.user ? (
            // User is signed in - show username
            <Link
              href={'/account/profile'}
              rel='noopener noreferrer'
              className='flex flex-row items-center gap-3'
            >
              {/* <PiUserCircleThin size={44} /> */}
              <Avatar
                size='md'
                name={username}
                src={avatarUrl}
                loading={avatarLoading}
              />

              <div className='flex flex-col'>
                <span className='font-medium  text-base'>{username}</span>
              </div>
            </Link>
          ) : (
            // User is not signed in - show login link
            <button
              className='flex flex-row items-center gap-2 border-none bg-transparent cursor-pointer'
              onClick={() => signIn('keycloak')}
              data-testid='signin_btn'
            >
              <div>
                <PiUserCircleThin size={44} />
              </div>

              {t('options.sign_in')}
            </button>
          )}
        </div>
        <div className='overflow-auto flex-1 max-h-[150px] md:max-h-full'>
          {/* Show Stats button - only for authorized users */}
          {isUserAuthorized && (
            <div className='py-1 px-1 md:py-1.5'>
              <DropdownMenuItem onSelect={() => setStatsModalOpen(true)}>
                <div className='p-1'>
                  <PiChartBarLight size={20} />
                </div>
                <span>{t('stats_modal.show_stats', 'Show Stats')}</span>
              </DropdownMenuItem>
            </div>
          )}

          {/* Tour button - hidden on mobile */}
          <div className='py-1 px-1 md:py-1.5 hidden md:block'>
            <DropdownMenuItem onSelect={handleStartTour}>
              <div className='p-1'>
                <PiShootingStarLight size={20} />
              </div>
              <span>{t('tours.mainTour.start_tour')}</span>
            </DropdownMenuItem>
          </div>

          <Separator />
          <div className='py-1 px-1 md:py-1.5'>
            <div className='flex gap-2 items-center px-1 py-1.5 mr-1'>
              <div className='p-1'>
                <PiGlobeLight size={20} />
              </div>
              <div className='grow font-medium text-sm'>
                {t('options.language')}
              </div>
              <Select
                value={i18n.language?.split('-')[0] ?? i18n.language}
                onValueChange={handleLangChange}
              >
                <SelectTrigger className='w-36' data-testid='language-switcher'>
                  <SelectValue>
                    {languageLabels[
                      i18n.language?.split('-')[0] ?? i18n.language
                    ] ?? i18n.language}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='en'>English</SelectItem>
                  <SelectItem value='ru'>Русский</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />
          <div className='py-1 px-1 md:py-1.5'>
            <div className='flex gap-2 items-center px-1 py-1.5 mr-1'>
              <div className='p-1'>
                <PiDownloadSimpleLight size={20} className='-rotate-90' />
              </div>
              <div className='grow flex items-center font-medium text-sm'>
                MIDI {t('options.input')}
                {/* <HoverCardMidiInput /> */}
                <div className='hidden md:block [@media(hover:hover)]:block [@media(pointer:fine)]:block'>
                  <TooltipMidiInput />
                </div>
              </div>
              <IconButton onClick={refreshMidiInputs}>
                <PiArrowsClockwiseLight size={20} />
              </IconButton>
              <Select
                value={selectedDeviceId}
                onValueChange={setSelectedDeviceId}
              >
                <SelectTrigger className='w-36'>
                  <SelectValue placeholder={t('options.absent')} />
                </SelectTrigger>
                <SelectContent fallbackMessage={t('options.absent')}>
                  {midiInputs.map((input, i) => (
                    <SelectItem key={i} value={input.id}>
                      {input.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex gap-2 items-center px-1 py-1.5  mr-1'>
              <div className='p-1'>
                <PiUploadSimpleLight size={20} className='rotate-90' />
              </div>
              <div className='grow font-medium text-sm'>
                MIDI {t('options.output')}
              </div>
              <IconButton
                onClick={() => {
                  setMuted((prev: boolean) => !prev);
                }}
              >
                {muted ? (
                  <PiSpeakerSlashLight size={20} />
                ) : (
                  <PiSpeakerHighLight size={20} />
                )}
              </IconButton>
              {/* <Select
              value={managerEntity?.midiManager?.outputId ?? undefined}
              onValueChange={handleOutputChange}
            >
              <SelectTrigger className='w-36'>
                <SelectValue>
                  {managerEntity?.midiManager?.outputId
                    ? midiStore.outputs.find(
                        (output) =>
                          output.id === managerEntity.midiManager.outputId
                      )?.name || 'None'
                    : 'None'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent fallbackMessage='Браузер'>
                {midiStore.outputs.map((output, i) => {
                  const value = output.id;
                  const text = output.name;
                  return (
                    <SelectItem key={i} value={value}>
                      {text}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select> */}
            </div>
          </div>
          <Separator />
          <div className='py-1 px-1 md:py-1.5'>
            <div className='flex gap-2 items-center  px-1 py-1.5 mr-1'>
              <label className='grow w-full font-medium text-sm flex items-center gap-2'>
                <div className='p-1'>
                  <PiWaveSineLight size={20} />
                </div>
                {t('options.check_for_ties')}
              </label>
              <Switch disabled />
            </div>
          </div>
          <Separator />
          <div className='py-1 px-1 md:py-1.5'>
            <div className='flex gap-2 items-center px-1 py-1.5 mr-1'>
              <div className='p-1'>
                <PiVideoCameraLight size={20} />
              </div>
              <label
                htmlFor='screen-recording-toggle'
                className='grow font-medium text-sm cursor-pointer'
              >
                {t('options.screen_recording', 'Screen Recording')}
              </label>
              <Switch
                id='screen-recording-toggle'
                checked={DebugModelEntity.screenRecordingEnabled}
                onCheckedChange={() => {
                  if (!isUserAuthorized) {
                    signIn('keycloak');
                    return;
                  }
                  DebugModelEntity.toggleScreenRecording();
                }}
                aria-label='Toggle screen recording'
              />
            </div>
            <DropdownMenuItem
              disabled={!hasDebugData}
              onSelect={async () => {
                OptionsModelEntity.showOptions = false;
                ManagerEntity.spinnerMsg = 'Saving report...';
                if (managerEntity.player?.eventsRecorder.debugData) {
                  await saveDebugData(
                    ManagerEntity.player?.eventsRecorder.debugData,
                    t
                  );
                  DebugModelEntity.refreshUUIDS();
                }
                ManagerEntity.spinnerMsg = '';
              }}
            >
              <div className='p-1'>
                <PiShareFatLight size={20} />
              </div>
              <span>{t('options.send_debug')}</span>
            </DropdownMenuItem>

            <DropdownMenuItem>
              <Link
                href='https://t.me/darpianocom'
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-2'
              >
                <div className='p-1'>
                  <PiTelegramLogoLight size={20} />
                </div>
                <div className='grow font-medium text-sm'>
                  {t('options.contact_support')}
                </div>
              </Link>
            </DropdownMenuItem>
          </div>
          {isAdminPage() && (
            <>
              <Separator />
              <div className='py-1 px-1 md:py-1.5'>
                <div className='flex gap-2 items-center px-1 py-1 md:py-1.5 mr-1'>
                  <div className='p-1'>
                    <PiFileCsvLight size={20} />
                  </div>
                  <div className='grow font-medium text-sm'>
                    {t('options.import_csv')}
                  </div>
                  <IconButton
                    onClick={() => {
                      importCsvInputRef.current?.click();
                    }}
                  >
                    <PiFileArrowUpLight size={20} />
                  </IconButton>
                  {/* Hidden file input */}
                  <input
                    type='file'
                    ref={importCsvInputRef}
                    accept='.csv'
                    className='hidden'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImportCSV(file);
                      }
                    }}
                  />
                </div>
                <div className='flex gap-2 items-center px-1 py-1 md:py-1.5 mr-1'>
                  <div className='p-1'>
                    <PiFileCsvLight size={20} />
                  </div>
                  <div className='grow font-medium text-sm'>
                    {t('options.export_csv')}
                  </div>
                  <IconButton onClick={handleExportCSV}>
                    <PiFileArrowDownLight size={20} />
                  </IconButton>
                </div>
              </div>
              <Separator />
              <div className='py-1 px-1 md:py-1.5'>
                <div className='flex gap-2 items-center px-1 py-1 md:py-1.5 mr-1'>
                  <div className='p-1'>
                    <PiFileCsvLight size={20} />
                  </div>
                  <div className='grow font-medium text-sm'>
                    {t('options.upload_seo')}
                  </div>
                  <IconButton
                    title='Upload SEO to Server'
                    onClick={() => {
                      uploadSeoInputRef.current?.click();
                    }}
                  >
                    <PiFileArrowUpLight size={20} />
                  </IconButton>
                  {/* Hidden file input */}
                  <input
                    type='file'
                    ref={uploadSeoInputRef}
                    accept='.csv'
                    className='hidden'
                    onChange={(e: any) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleUploadSEO(file);
                      }
                    }}
                  />
                </div>
                <div className='flex gap-2 items-center px-1 py-1 md:py-1.5 mr-1'>
                  <div className='p-1'>
                    <PiFileCsvLight size={20} />
                  </div>
                  <div className='grow font-medium text-sm'>
                    {t('options.download_seo')}
                  </div>
                  <IconButton onClick={handleDownloadSEO} title='Download Latest SEO in DB'>
                    <PiFileArrowDownLight size={20} />
                  </IconButton>
                </div>
                <div className='flex gap-2 items-center px-1 py-1 md:py-1.5 mr-1'>
                  <div className='p-1'>
                    <PiFileCsvLight size={20} />
                  </div>
                  <div className='grow font-medium text-sm'>
                    {t('options.export_seo')}
                  </div>
                  <IconButton onClick={handleExportSEO} title='Export Metadata SEO from Sitemap'>
                    <PiFileArrowDownLight size={20} />
                  </IconButton>
                </div>
              </div>
            </>
          )}
          <div className='py-1 px-1 md:py-1.5'>
            <Separator />
           {session?.data?.user ? (
            <>
            <div className='py-1 px-1 md:py-1.5 flex justify-between items-center gap-2 cursor-pointer'>
               <div className="flex gap-2 items-center">
                <div className="p-1">
                  <PiGameControllerThin size={20} />
                </div>
                <label 
                htmlFor='gamification-toggle' 
                className='grow w-full font-medium text-sm flex items-center gap-2 cursor-pointer'>
                  {t('gamification.toggle_gamification')}
                </label>
               </div>
               <div>
                 <Switch
                   id='gamification-toggle'
                   checked={gamificationEnabled}
                   onCheckedChange={() => toggleGamification()}
                   aria-label='Toggle gamification'
                   />
               </div>
            </div>
            {gamificationEnabled && (
              <TooltipSaveProgress>
                <div className='py-1 px-1 md:py-1.5 flex justify-between items-center gap-2'>
                  <div className="flex gap-2 items-center cursor-pointer">
                    <div className="p-1">
                      <FiSave size={20} />
                    </div>
                    <label 
                    htmlFor='gamification-saving-toggle' 
                    className='grow w-full font-medium text-sm flex items-center gap-2 cursor-pointer'>
                      {t('gamification.toggle_saving')}
                    </label>
                  </div>
                  <div>
                    <Switch
                      id='gamification-saving-toggle'
                      checked={saveProgressEnabled}
                      onCheckedChange={() => toggleSaveProgress()}
                      aria-label='Toggle gamification'
                      />
                  </div>
                </div>
              </TooltipSaveProgress>
              )
            }
            </>
            ) : (
              <div className='py-1 px-1 md:py-1.5'>
                <div className="flex gap-2 items-center">
                  <div className="p-1">
                    <PiGameControllerThin size={20} />
                  </div>
                  <label
                  className='grow w-full font-medium text-sm flex items-center gap-2 cursor-pointer'
                  onClick={() => signIn('keycloak')}
                  >{t('gamification.toggle_sign-in')}</label>
                </div>
              </div>
            )}
          </div>
          {/* The bubbles should contain not noteSucessCount but rather noteSucessCount * multiplier */}
          {/* Select the same sample and play some notes -> select the same sample -> check if it was saved */}
          {session?.data?.user && (
            <>
              <Separator />
              <div className='py-1 px-1 md:py-1.5'>
                <DropdownMenuItem data-testid="options_logout_btn" onClick={() => signOut()}>
                  <div className='p-1'>
                    <PiSignOutLight size={20} />
                  </div>
                  {t('options.sign_out')}
                </DropdownMenuItem>
              </div>
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    <StatsModal open={statsModalOpen} onOpenChange={setStatsModalOpen} />
    </>
  );
});